
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Currency } from '@/lib/types';
import { CurrencyForm } from './CurrencyForm';
import { useState } from 'react';
import { Button } from '../ui/button';
import { AlertTriangle, Plus, List } from 'lucide-react';
import { CurrencySelector } from './CurrencySelector';
import { useCurrencies } from '@/hooks/useCurrencies';
import { useTranslation } from 'react-i18next';
import { Alert, AlertDescription } from '@/components/ui/alert';

interface CurrencyModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  currency?: Currency;
  onSuccess?: (currency: Currency) => void;
}

type CreationMode = 'manual' | 'select';

export function CurrencyModal({
  open,
  onOpenChange,
  currency,
  onSuccess,
}: CurrencyModalProps) {
  const { t } = useTranslation();
  const [mode, setMode] = useState<CreationMode>(currency ? 'manual' : 'select');
  const { useCurrenciesQuery, useCreateCurrencyMutation } = useCurrencies();
  const { data: existingCurrencies = [] } = useCurrenciesQuery();
  const createCurrency = useCreateCurrencyMutation();

  const handleSelectCurrency = async (selectedCurrency: Omit<Currency, "id" | "user_id">) => {
    try {
      const newCurrency = await createCurrency.mutateAsync(selectedCurrency);
      if (onSuccess) {
        onSuccess(newCurrency);
      }
    } catch (error) {
      console.error('Error creating currency:', error);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>
            {currency ? t('editCurrency') : t('addNewCurrency')}
          </DialogTitle>
          <DialogDescription>
            {currency 
              ? t('updateCurrencyDetails') 
              : t('addNewCurrencyDescription')}
          </DialogDescription>
        </DialogHeader>

        <div className="max-h-[calc(90vh-180px)] overflow-y-auto pr-1">
          <Alert variant="warning" className="mb-4">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              {t('currencyRateDisclaimer', 'Currency exchange rates are approximate and updated periodically. Contract values shown in different currencies are estimates and may not reflect exact rates at the time of payment.')}
            </AlertDescription>
          </Alert>

          {!currency && (
            <div className="flex gap-4 mb-6">
              <Button
                variant={mode === 'select' ? 'default' : 'outline'}
                className="flex-1"
                onClick={() => setMode('select')}
              >
                <List className="mr-2 h-4 w-4" />
                {t('selectFromList')}
              </Button>
              <Button
                variant={mode === 'manual' ? 'default' : 'outline'}
                className="flex-1"
                onClick={() => setMode('manual')}
              >
                <Plus className="mr-2 h-4 w-4" />
                {t('createManually')}
              </Button>
            </div>
          )}

          {mode === 'manual' ? (
            <CurrencyForm
              currency={currency}
              onSuccess={(newCurrency) => {
                if (onSuccess) {
                  onSuccess(newCurrency);
                }
                onOpenChange(false);
              }}
              onCancel={() => onOpenChange(false)}
            />
          ) : (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                {t('selectCurrencyFromList', 'Click on a currency to add it to your list of available currencies.')}
              </p>
              <CurrencySelector 
                mode="selection"
                onSelect={handleSelectCurrency} 
                existingCurrencies={existingCurrencies}
              />
            </div>
          )}
        </div>

        {mode === 'select' && (
          <DialogFooter>
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              {t('close', 'Close')}
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}
